import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AncillariesSwitch from '../../ui/atoms/AncillariesSwitch';
import useInsuranceConfig from '../../hooks/useInsuranceConfig';

/**
 * InsuranceAncillarySwitch component.
 *
 * @param {object} props - checked, isLoading, onChange, message, title, price, singlePrice, plusTaxes
 * @param {boolean} props.checked - true if the insurance is checked
 * @param {boolean} props.isLoading - true if the insurance is loading
 * @param {function} props.onChange - the function to call when the insurance is changed
 * @param {string} props.message - the message to show
 * @param {string} props.title - the title to show (optional, uses titleContext from config if not provided)
 * @param {string} props.price - the price to show
 * @param {boolean} props.singlePrice - true if each abbr text can be shown
 * @param {boolean} props.plusTaxes - true if plus taxes text can be shown
 * @param {string} props.id - the id to show
 * @param {string} props.fullPrice - the full price including taxes
 * @param {boolean} props.disabled - whether the switch is disabled
 * @returns {React.Component} - The insurance ancillary switch component
 */
const InsuranceAncillarySwitch = ({
  checked,
  isLoading,
  message,
  title,
  price,
  singlePrice,
  onChange,
  plusTaxes,
  id,
  fullPrice,
  disabled,
}) => {
  const {
    icon,
    detailsUrl,
    price: fixedInsurancePrice,
    coverage,
    disclaimerConfig,
    titleContext,
    taxRate,
  } = useInsuranceConfig();
  const coverageItems = coverage?.coverageItems || [];
  const { t } = useTranslation();

  const insuranceTitle =
    title || t('insurance:Insurance Checkbox Title', { context: titleContext });

  return (
    <AncillariesSwitch
      icon={icon}
      id={id}
      href={detailsUrl}
      linkText={detailsUrl ? t('insurance:terms_conditions') : ''}
      checked={checked}
      isLoading={isLoading}
      onChange={onChange}
      message={message}
      title={insuranceTitle}
      price={price ?? fixedInsurancePrice}
      singlePrice={singlePrice}
      plusTaxes={plusTaxes}
      fullPrice={fullPrice}
      coverageItems={coverageItems}
      disabled={disabled}
      taxRate={taxRate}
      disclaimerConfig={disclaimerConfig}
    />
  );
};

InsuranceAncillarySwitch.propTypes = {
  checked: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  singlePrice: PropTypes.bool,
  plusTaxes: PropTypes.bool,
  id: PropTypes.string,
  fullPrice: PropTypes.string,
  disabled: PropTypes.bool,
};

InsuranceAncillarySwitch.defaultProps = {
  title: null,
};

export default InsuranceAncillarySwitch;
